'use client';

import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { getCalApi } from '@calcom/embed-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTheme } from 'next-themes';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';

import { LandingIcons } from '../icons';
import BlurFade from '../magicui/blur-fade';
import GlowingBadge from '../ui/glowing-badge';
import Certificates from './certificates';

const CtaSchema = z.object({
  email: z.email({
          error: 'Please enter a valid email address.'
    }),
});

type CtaSectionProps = {
  id?: string;
  heading?: string;
  content?: ReactNode;
  buttonText?: string;
  imageSrc?: string;
  imageAlt?: string;
  inputPlaceholder?: string;
};

export default function CtaSection({
  id = 'contact-section',
  heading,
  content,
  buttonText,
  inputPlaceholder,
  imageSrc,
  imageAlt,
}: CtaSectionProps) {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();
  const form = useForm({
    resolver: zodResolver(CtaSchema),
    defaultValues: {
      email: '',
    },
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  const onSubmit = async (data: z.infer<typeof CtaSchema>) => {
    const cal = await getCalApi();
    cal('ui', {
      styles: { branding: { brandColor: '#000000' } },
      hideEventTypeDetails: false,
      layout: 'month_view',
      theme: resolvedTheme as 'light' | 'dark',
    });
    cal('modal', {
      calLink: process.env.NEXT_PUBLIC_DEMO_CAL_LINK || '',
      config: {
        email: data.email,
      },
    });
  };

  return (
    <section id={id} className="p-2.5 lg:p-4">
      <div className="mx-auto flex max-w-1440 items-center justify-center rounded-[20px] bg-[#141414] lg:rounded-[40px]">
        <BlurFade inView>
          <div className="max-w-full overflow-hidden rounded-3xl bg-background-6">
            <div className="mx-auto grid h-full grid-cols-1 items-center lg:grid-cols-2">
              <div className="px-6 pb-0 pt-10 lg:py-25 lg:pl-20">
                <div className="mb-5 flex lg:mb-4">
                  <GlowingBadge>
                    <LandingIcons.demo />
                    <span>Get Started</span>
                  </GlowingBadge>
                </div>
                <h2 className="text-[32px] font-semibold leading-9 text-[#e6e6e6] lg:text-5xl lg:leading-[62px]">
                  {heading}
                </h2>
                <p className="mb-11 mt-4 text-sm leading-5 text-white/50 lg:mb-12 lg:mt-9 lg:pr-5 lg:text-base lg:leading-normal">
                  {content}
                </p>
                {mounted && (
                  <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit(onSubmit)}
                      className="mb-5 mt-3 flex flex-col gap-4 lg:mb-17 lg:flex-row lg:gap-3"
                    >
                      <label htmlFor="email" className="sr-only" id="email-label">Email</label>
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl className="relative">
                              <Input
                                id="email"
                                {...field}
                                type="email"
                                className="border-1 h-11 w-full rounded-[62px] border border-white/10 bg-[#141414] px-[18px] py-3 text-white lg:w-[344px]"
                                placeholder={inputPlaceholder}
                                style={{
                                  background:
                                    'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.00) 123.75%), rgba(255, 255, 255, 0.12)',
                                  boxShadow:
                                    '0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px -1px rgba(0, 0, 0, 0.10)',
                                }}
                                aria-describedby="email-label"
                              />
                            </FormControl>
                            <FormMessage className="absolute pl-5" />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="submit"
                        variant="default"
                        className="flex h-11 w-full gap-2 rounded-3xl bg-white text-[#181618] lg:w-auto lg:text-sm lg:font-semibold"
                      >
                        {buttonText}
                      </Button>
                    </form>
                  </Form>
                )}
                <Certificates />
              </div>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={imageSrc}
                alt={imageAlt}
                className="ml-6 mt-7 aspect-auto w-[578px] max-w-none rounded-xl shadow-2xl sm:h-full sm:w-full lg:ml-24 lg:mt-28 lg:max-w-full lg:object-cover lg:object-left-top"
              />
            </div>
          </div>
        </BlurFade>
      </div>
    </section>
  );
}
